import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { IPortfolioEpicCompletion, IPortfolioEpicCompletionResponse } from '@skystone/shared';
import { MetricLevelBadge, Tooltip as UITooltip } from '@/components/ui';

// ---------------------------------------------------------------------------
// Shared helpers for "Epic Completion by Stream" — used by both Management
// Dashboard's Delivery tab and Metric View's Epic Completion card. Extracted
// here to keep SonarCloud new-code duplication under 3%.
// ---------------------------------------------------------------------------

// Three-segment chart colours. The chart shows each epic's state as:
// settled before the period (blue) → closed during the period (emerald
// accent) → not yet done at end of period (dark grey). Stack order
// left-to-right reads as time progression: past → period → open.
export const EPIC_DONE_BEFORE_COLOR = 'var(--color-chart-1)';
export const EPIC_DONE_IN_PERIOD_COLOR = 'var(--color-chart-2)';
export const EPIC_REMAINING_COLOR = 'var(--color-muted-foreground)';

export interface IEpicChartDatum {
  label: string;
  DoneBefore: number;
  DoneInPeriod: number;
  Remaining: number;
  pct: number;
  done: number;
  doneInPeriod: number;
  total: number;
  level?: string | null;
}

export interface IEpicSummary {
  totalEpics: number;
  epicsDone: number;
  epicsNotDone: number;
  issuesDone: number;
  issuesNotDone: number;
  /**
   * Sum of `doneInPeriod` across the visible epics. Drives the new "Done
   * this period — N" header chip. Falls back to 0 when the response
   * predates PRODOPSAI-380 and lacks the field.
   */
  issuesDoneInPeriod: number;
}

export function computeEpicSummary(epics: IPortfolioEpicCompletion[]): IEpicSummary {
  let epicsDone = 0;
  let issuesDone = 0;
  let issuesDoneInPeriod = 0;
  let issuesTotal = 0;
  for (const e of epics) {
    // Derive the period-end "done" total from the buckets so every chip in
    // the header agrees with the bar segments. For current windows (where
    // the SQL invariant `done = doneBefore + doneInPeriod` holds) this
    // equals e.done. For historical windows or legacy responses missing
    // the new fields, this stays consistent with what the chart paints.
    const doneInPeriod = e.doneInPeriod ?? 0;
    const doneBefore = e.doneBefore ?? Math.max(0, e.done - doneInPeriod);
    const doneAtPeriodEnd = doneBefore + doneInPeriod;
    issuesDone += doneAtPeriodEnd;
    issuesDoneInPeriod += doneInPeriod;
    issuesTotal += e.total;
    if (e.total > 0 && doneAtPeriodEnd === e.total) epicsDone++;
  }
  return {
    totalEpics: epics.length,
    epicsDone,
    epicsNotDone: epics.length - epicsDone,
    issuesDone,
    issuesDoneInPeriod,
    issuesNotDone: Math.max(0, issuesTotal - issuesDone),
  };
}

const CHIP_BASE = 'inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-xs normal-case';
const CHIP_NEUTRAL = 'border-border text-foreground';
const CHIP_DONE = 'border-success/30 bg-success/10 text-success';
const CHIP_NOT_DONE = 'border-warning/30 bg-warning/10 text-warning';
// CHIP_ACCENT matches the green "Done this period" bar segment — same
// emerald family as --color-chart-2.
const CHIP_ACCENT = 'border-success/30 bg-success/10 text-success';

interface IEpicHeaderStatsProps {
  summary: IEpicSummary;
  /**
   * When false, drop the "Done this period" chip. Used by the narrow card
   * variants (miniList / list) where the third chip causes the header to
   * wrap unattractively. Defaults to true (chart variant always shows it).
   */
  showDoneInPeriodChip?: boolean;
}

export function EpicHeaderStats({
  summary,
  showDoneInPeriodChip = true,
}: Readonly<IEpicHeaderStatsProps>) {
  if (summary.totalEpics === 0) return null;
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className={`${CHIP_BASE} ${CHIP_NEUTRAL}`}>
        <span className="text-muted-foreground">Epics</span>
        <span className="font-semibold">{summary.totalEpics.toLocaleString()}</span>
      </span>
      <span className={`${CHIP_BASE} ${CHIP_DONE}`}>
        <span className="opacity-80">Done</span>
        <span className="font-semibold">{summary.epicsDone.toLocaleString()}</span>
      </span>
      <span className={`${CHIP_BASE} ${CHIP_NOT_DONE}`}>
        <span className="opacity-80">Not done</span>
        <span className="font-semibold">{summary.epicsNotDone.toLocaleString()}</span>
      </span>
      <span className="mx-1 text-border" aria-hidden="true">|</span>
      <span className="text-xs font-medium text-muted-foreground">Issues:</span>
      <span className={`${CHIP_BASE} ${CHIP_DONE}`}>
        <span className="opacity-80">Done</span>
        <span className="font-semibold">{summary.issuesDone.toLocaleString()}</span>
      </span>
      {showDoneInPeriodChip && (
        <span className={`${CHIP_BASE} ${CHIP_ACCENT}`}>
          <span className="opacity-80">Done this period</span>
          <span className="font-semibold">{summary.issuesDoneInPeriod.toLocaleString()}</span>
        </span>
      )}
      <span className={`${CHIP_BASE} ${CHIP_NOT_DONE}`}>
        <span className="opacity-80">Not done</span>
        <span className="font-semibold">{summary.issuesNotDone.toLocaleString()}</span>
      </span>
    </div>
  );
}

export function EpicCompletionTooltip({
  active,
  payload,
}: Readonly<{ active?: boolean; payload?: Array<{ payload: IEpicChartDatum }> }>) {
  if (!active || !payload?.length) return null;
  const epic = payload[0].payload;
  return (
    <div className="rounded-md border bg-popover px-2 py-1.5 text-xs shadow-md">
      <p className="font-medium text-foreground">{epic.label}</p>
      <p className="text-muted-foreground">
        {epic.pct}% ({epic.done}/{epic.total})
      </p>
      <p className="text-muted-foreground">
        Done before: <span className="tabular-nums">{epic.DoneBefore}</span>
        {' · '}
        Done this period: <span className="tabular-nums">{epic.DoneInPeriod}</span>
        {' · '}
        Remaining: <span className="tabular-nums">{epic.Remaining}</span>
      </p>
      {epic.level && (
        <p className="text-muted-foreground">
          Level: <MetricLevelBadge level={epic.level} />
        </p>
      )}
    </div>
  );
}

export function toEpicChartData(epics: IPortfolioEpicCompletion[]): IEpicChartDatum[] {
  return epics.map((e) => {
    // Derive the three buckets. When the response includes the new fields
    // (PRODOPSAI-380+), we trust them. When it doesn't (legacy fixtures /
    // old responses), all of `done` falls into "Done before" and the
    // "Done this period" segment is empty — same visual columns as the old
    // two-segment chart, just with the segment relabelled.
    const doneInPeriod = e.doneInPeriod ?? 0;
    const doneBefore = e.doneBefore ?? Math.max(0, e.done - doneInPeriod);
    const doneAtPeriodEnd = doneBefore + doneInPeriod;
    // Remaining = total minus the two "done" buckets. For current windows
    // this equals `is_done = false`; for historical windows it captures
    // pending work plus post-window closures (see PRODOPSAI-380 SQL).
    const remaining = Math.max(0, e.total - doneAtPeriodEnd);
    // Derive `done` and `pct` from the buckets too, so the tooltip's
    // "X% (done/total)" line agrees with the bar segments below it.
    // Otherwise a historical-window epic could show 100% in the tooltip
    // header while the bar still has a non-zero Remaining segment.
    const pct = e.total > 0 ? Math.round((doneAtPeriodEnd / e.total) * 100) : 0;
    return {
      label: `${e.epicName} (${e.productName})`,
      DoneBefore: doneBefore,
      DoneInPeriod: doneInPeriod,
      Remaining: remaining,
      pct,
      done: doneAtPeriodEnd,
      doneInPeriod,
      total: e.total,
      level: e.level,
    };
  });
}

interface IEpicCompletionChartProps {
  data: IPortfolioEpicCompletionResponse;
  /** When true, use the compact Delivery-tab styling (smaller Y-axis tick, narrower label column, taller per-row spacing). Default is the Metric-View styling (wider labels, no inner scroll). */
  compact?: boolean;
}

interface IEpicYAxisTickProps {
  x?: number;
  y?: number;
  payload?: { value: string };
  tickWidth: number;
  fontSize: number;
}

// Average glyph width at the SVG default font is ~0.58em. Used to estimate
// how many characters fit into the tick column before adding an ellipsis.
// Subtracts 8px for the chart's internal padding between label and bar.
export function estimateMaxChars(tickWidth: number, fontSize: number): number {
  return Math.max(5, Math.floor((tickWidth - 8) / (fontSize * 0.58)));
}

// ---------------------------------------------------------------------------
// Inline segment-value label
//
// Recharts' default label on a stacked <Bar> can render the cumulative
// stack value at the segment's right edge — so for an epic with
// doneBefore=141 and doneInPeriod=15, the green segment ends up labelled
// "156" (= 141+15) instead of "15". That misleads: the chart's headline
// is "what moved this period", and the green segment IS that answer. The
// label must show the segment's own value, not the running total.
//
// Implementation: closure over `chartData` keyed by `index` — recharts
// reliably injects `index` into label props, and we look up the segment
// value ourselves. Don't try to read `payload` from props; recharts'
// element-form label props don't reliably include the data row.
// ---------------------------------------------------------------------------

type SegmentDataKey = 'DoneBefore' | 'DoneInPeriod' | 'Remaining';

interface ISegmentLabelProps {
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  index?: number;
}

export function makeSegmentValueLabel(dataKey: SegmentDataKey, rows: readonly IEpicChartDatum[]) {
  function SegmentValueLabel({ x = 0, y = 0, width = 0, height = 0, index }: Readonly<ISegmentLabelProps>) {
    if (typeof index !== 'number') return null;
    const row = rows[index];
    if (!row) return null;
    const value = row[dataKey];
    if (typeof value !== 'number' || value === 0) return null;
    return (
      <text
        x={x + width - 4}
        y={y + height / 2}
        fill="white"
        fontSize={11}
        fontWeight={600}
        textAnchor="end"
        dominantBaseline="middle"
      >
        {value}
      </text>
    );
  }
  return SegmentValueLabel;
}

export function EpicYAxisTick({ x = 0, y = 0, payload, tickWidth, fontSize }: Readonly<IEpicYAxisTickProps>) {
  const full = payload?.value ?? '';
  const maxChars = estimateMaxChars(tickWidth, fontSize);
  const needsTruncation = full.length > maxChars;
  const display = needsTruncation ? `${full.slice(0, maxChars - 1)}…` : full;
  const height = Math.max(16, fontSize + 6);

  return (
    <foreignObject x={x - tickWidth} y={y - height / 2} width={tickWidth} height={height}>
      <div className="flex h-full items-center justify-end pr-2 text-muted-foreground" style={{ fontSize }}>
        {needsTruncation ? (
          <UITooltip content={full} side="right">
            <span className="cursor-default whitespace-nowrap">{display}</span>
          </UITooltip>
        ) : (
          <span className="whitespace-nowrap">{display}</span>
        )}
      </div>
    </foreignObject>
  );
}

export function EpicCompletionChart({
  data,
  compact = false,
}: Readonly<IEpicCompletionChartProps>) {
  const chartData = toEpicChartData(data.epics);
  if (chartData.length === 0) return null;

  const height = Math.max(220, chartData.length * (compact ? 35 : 32));
  const tickWidth = compact ? 180 : 340;
  const tickFontSize = compact ? 11 : 12;
  // Bind label components to the resolved chart data so they can look up
  // the segment value by `index` instead of relying on recharts to pass
  // `payload` (which it doesn't always do for element-form labels).
  const DoneBeforeLabel = makeSegmentValueLabel('DoneBefore', chartData);
  const DoneInPeriodLabel = makeSegmentValueLabel('DoneInPeriod', chartData);
  const RemainingLabel = makeSegmentValueLabel('Remaining', chartData);

  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={chartData} layout="vertical" margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
        <XAxis type="number" tick={{ fontSize: 11 }} className="fill-muted-foreground" />
        <YAxis
          type="category"
          dataKey="label"
          tick={<EpicYAxisTick tickWidth={tickWidth} fontSize={tickFontSize} />}
          width={tickWidth}
          interval={0}
          className="fill-muted-foreground"
        />
        <Tooltip content={<EpicCompletionTooltip />} />
        <Legend wrapperStyle={{ fontSize: 11 }} />
        <Bar
          dataKey="DoneBefore"
          name="Done before"
          stackId="a"
          fill={EPIC_DONE_BEFORE_COLOR}
          label={<DoneBeforeLabel />}
        />
        <Bar
          dataKey="DoneInPeriod"
          name="Done this period"
          stackId="a"
          fill={EPIC_DONE_IN_PERIOD_COLOR}
          label={<DoneInPeriodLabel />}
        />
        <Bar
          dataKey="Remaining"
          name="Remaining"
          stackId="a"
          fill={EPIC_REMAINING_COLOR}
          label={<RemainingLabel />}
        />
      </BarChart>
    </ResponsiveContainer>
  );
}
